import React, { useEffect, useMemo, useRef, useState } from "react";
import "katex/dist/katex.min.css";
import { BlockMath } from "react-katex";

/**
 * Generic Blockbusters (Compound + Simple Interest)
 * - Welcome screen first
 * - Start Game -> main grid
 * - 20 unique questions (4x5)
 * - 2 tries per tile; correct turns BLUE, fail turns WHITE
 * - Win if you connect left column to right column via adjacent hex neighbors
 */

const tileGrid = [
  ["1", "2", "3", "4", "5"],
  ["6", "7", "8", "9", "10"],
  ["11", "12", "13", "14", "15"],
  ["16", "17", "18", "19", "20"],
];

// Your existing tile images
const GOLD_TILE =
  "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fhex_tile.png?alt=media&token=bac8036f-49f8-4333-90bf-f00ebfd7e2c0";
const BLUE_TILE =
  "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fhex_tile_blue.png?alt=media&token=8b270b4b-ceea-482b-b7d8-e3d04a06c238";
const WHITE_TILE =
  "https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2Fhex_tile_white.png?alt=media&token=a2463879-326d-4664-895c-6bb8adf8fe59";

// --------- helpers ----------
const norm = (s) =>
  String(s ?? "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/,/g, "")
    .toLowerCase();

// Accept “£” or no “£”, allow optional trailing zeros, etc.
const normalizeMoney = (s) => norm(s).replace(/^£/, "").replace(/^\\pounds/, "");

const almostEqualNumber = (aStr, bStr) => {
  const a = Number(aStr);
  const b = Number(bStr);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) < 1e-9;
};

const answerMatches = (playerAnswer, acceptedAnswers = []) => {
  const p = String(playerAnswer ?? "").trim();
  if (!p) return false;

  // Try numeric match
  const pNum = Number(p.replace(/£|,/g, ""));
  const pMoney = normalizeMoney(p);

  return acceptedAnswers.some((acc) => {
    const accStr = String(acc);

    // If accepted is something like "£145" or "145"
    const accMoney = normalizeMoney(accStr);

    // money/number compare first
    const accNum = Number(accStr.replace(/£|,/g, "").replace(/\\pounds/g, ""));
    if (Number.isFinite(pNum) && Number.isFinite(accNum) && almostEqualNumber(pNum, accNum)) return true;

    // direct normalized string compare
    if (pMoney && accMoney && pMoney === accMoney) return true;

    // fallback strict normalized
    if (norm(p) === norm(accStr)) return true;

    return false;
  });
};

// KaTeX-ish detection (only to decide if we render BlockMath for a line)
const looksLikeLatex = (s = "") => /\\[a-zA-Z]+|[_^]|\\frac|\\times|\\div/.test(s);

// Neighbor logic for hex grid (your 4x5)
const getNeighbors = (row, col) => {
  const evenRowDirs = [
    [-1, 0], [-1, 1],
    [0, -1], [0, 1],
    [1, 0], [1, 1],
  ];
  const oddRowDirs = [
    [-1, -1], [-1, 0],
    [0, -1], [0, 1],
    [1, -1], [1, 0],
  ];
  const dirs = row % 2 === 0 ? evenRowDirs : oddRowDirs;
  return dirs
    .map(([dr, dc]) => [row + dr, col + dc])
    .filter(([r, c]) => r >= 0 && r < 4 && c >= 0 && c < 5);
};

const checkForWin = (blueTilesSet) => {
  // blueTilesSet contains keys like "row-col" that are BLUE (correct)
  const visited = new Set();

  const dfs = (r, c) => {
    const key = `${r}-${c}`;
    if (visited.has(key)) return false;
    visited.add(key);

    if (c === 4) return true; // reached rightmost col

    for (const [nr, nc] of getNeighbors(r, c)) {
      const nKey = `${nr}-${nc}`;
      if (blueTilesSet.has(nKey)) {
        if (dfs(nr, nc)) return true;
      }
    }
    return false;
  };

  // start from any BLUE tile in the leftmost column (col 0)
  for (let r = 0; r < 4; r++) {
    const startKey = `${r}-0`;
    if (blueTilesSet.has(startKey)) {
      if (dfs(r, 0)) return true;
    }
  }
  return false;
};

// --------- QUESTIONS (20 unique) ----------
const QUESTIONS = [
  // 1–10: simple interest (mix of find interest, amount, rate, time, principal)
  {
    id: "q1",
    promptLines: ["Simple interest.", "£800 at 6% per year for 5 years.", "Find the interest earned."],
    accepted: ["240", "£240"],
  },
  {
    id: "q2",
    promptLines: ["Simple interest.", "£1,500 at 4% per year for 3 years.", "Find the total amount."],
    accepted: ["1680", "£1680"],
  },
  {
    id: "q3",
    promptLines: ["Simple interest.", "Interest of £90 is earned at 5% per year.", "How long was the money invested if the principal was £600?"],
    accepted: ["3", "3 years", "3years"],
  },
  {
    id: "q4",
    promptLines: ["Simple interest.", "£2,400 grows to £2,760 in 2 years.", "Find the annual rate."],
    accepted: ["7.5", "7.5%", "7.5 percent", "7.5percent"],
  },
  {
    id: "q5",
    promptLines: ["Simple interest.", "£950 at 8% per year for 18 months.", "Find the interest earned."],
    accepted: ["114", "£114"],
  },
  {
    id: "q6",
    promptLines: ["Simple interest.", "£3,200 at 3.5% per year for 4 years.", "Find the total amount."],
    accepted: ["3648", "£3648"],
  },
  {
    id: "q7",
    promptLines: ["Simple interest.", "£720 earns £129.60 interest in 3 years.", "Find the annual rate."],
    accepted: ["6", "6%", "6 percent", "6percent"],
  },
  {
    id: "q8",
    promptLines: ["Simple interest.", "£1,250 at 9% per year.", "How long to earn £337.50 interest?"],
    accepted: ["3", "3 years", "3years"],
  },
  {
    id: "q9",
    promptLines: ["Simple interest.", "£540 at 5% per year for 2.5 years.", "Find the total amount."],
    accepted: ["607.5", "£607.50", "£607.5"],
  },
  {
    id: "q10",
    promptLines: ["Simple interest.", "A loan of £4,000 is repaid as £4,720 after 3 years.", "Find the annual rate."],
    accepted: ["6", "6%", "6 percent", "6percent"],
  },

  // 11–20: compound interest (mix of amount, interest, rate, time, depreciation)
  {
    id: "q11",
    promptLines: ["Compound interest (annual).", "£1,000 at 5% for 2 years.", "Find the final amount."],
    accepted: ["1102.5", "£1102.50", "£1102.5"],
  },
  {
    id: "q12",
    promptLines: ["Compound interest (annual).", "£800 at 10% for 3 years.", "Find the final amount."],
    accepted: ["1064.8", "£1064.80", "£1064.8"],
  },
  {
    id: "q13",
    promptLines: ["Compound interest (annual).", "£2,000 at 4% for 5 years.", "Find the final amount (to 2 d.p.)."],
    accepted: ["2433.31", "£2433.31"],
  },
  {
    id: "q14",
    promptLines: ["Compound interest (annual).", "£1,600 increases by 6% each year for 4 years.", "Find the final amount (to 2 d.p.)."],
    accepted: ["2019.48", "£2019.48"],
  },
  {
    id: "q15",
    promptLines: ["Ryan invested £500 at a compound interest rate of r% per annum.",
      "After 2 years, he has £605.00 in his account.",
      "Find the value of r."],
    accepted: ["10", "10%", "10percent", "10 percent"],
  },
  {
    id: "q16",
    promptLines: ["£1,200 is invested at a compound interest rate of 3% for 3 years.", "How much interest is earned?"],
    accepted: ["111.27", "£111.27"],
  },
  {
    id: "q17",
    promptLines: ["A car worth £9,000 depreciates by 15% each year.", "Find its value after 2 years."],
    accepted: ["£6502.50", "6502.50"],
  },
  {
    id: "q18",
    promptLines: ["£3,500 decreases by 8% each half-year.", "Find the value after 3 years."],
    accepted: ["2122.24", "£2122.24"],
  },
  {
    id: "q19",
    promptLines: ["Compound interest (annual).", "£1,500 at 7% per year.", "After how many years will it exceed £1,837.35?"],
    accepted: ["3", "3 years", "3years"],
  },
  {
    id: "q20",
    promptLines: ["Compound interest (annual).", "£2,500 at 2.5% for 6 years.", "Find the final amount (to 2 d.p.)."],
    accepted: ["2899.07", "£2899.07"],
  },
];

function BlockbustersCompoundInterest() {
  // welcome -> game switch
  const [stage, setStage] = useState("welcome"); // "welcome" | "game"

  // tile mapping: row-col -> question
  const tileQuestions = useMemo(() => {
    const map = {};
    let i = 0;
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 5; c++) {
        map[`${r}-${c}`] = QUESTIONS[i];
        i++;
      }
    }
    return map;
  }, []);

  // gameplay states
  const [showModal, setShowModal] = useState(false);
  const [selectedTile, setSelectedTile] = useState(null); // {r,c}
  const [attemptsOnThisTile, setAttemptsOnThisTile] = useState(0);
  const [playerAnswer, setPlayerAnswer] = useState("");
  const [playerWorking, setPlayerWorking] = useState("");
  const [feedback, setFeedback] = useState(null); // "correct" | "incorrect" | null

  const [blueTiles, setBlueTiles] = useState(() => new Set());  // correct
  const [whiteTiles, setWhiteTiles] = useState(() => new Set()); // failed/locked
  const [hasWon, setHasWon] = useState(false);

  const openTile = (r, c) => {
    const key = `${r}-${c}`;
    if (blueTiles.has(key) || whiteTiles.has(key)) return; // locked/already done

    setSelectedTile({ r, c });
    setAttemptsOnThisTile(0);
    setPlayerAnswer("");
    setPlayerWorking("");
    setFeedback(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTile(null);
    setFeedback(null);
    setPlayerAnswer("");
    setPlayerWorking("");
    setAttemptsOnThisTile(0);
  };

  const currentQ = selectedTile ? tileQuestions[`${selectedTile.r}-${selectedTile.c}`] : null;

  const submitAnswer = () => {
    if (!currentQ || !selectedTile) return;

    const correct = answerMatches(playerAnswer, currentQ.accepted);

    if (correct) {
      setFeedback("correct");

      setTimeout(() => {
        const key = `${selectedTile.r}-${selectedTile.c}`;
        setBlueTiles((prev) => {
          const next = new Set(prev);
          next.add(key);
          // check win after update
          if (checkForWin(next)) setHasWon(true);
          return next;
        });
        closeModal();
      }, 900);

      return;
    }

    // incorrect
    setFeedback("incorrect");
    setAttemptsOnThisTile((prev) => prev + 1);

    // if this was second try -> lock white
    const nextAttempts = attemptsOnThisTile + 1;
    if (nextAttempts >= 2) {
      setTimeout(() => {
        const key = `${selectedTile.r}-${selectedTile.c}`;
        setWhiteTiles((prev) => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
        closeModal();
      }, 1200);
    } else {
      // let them try again
      setTimeout(() => setFeedback(null), 900);
    }
  };

  // --- tile sizing/position (kept simple) ---
  const SCALE = 1;
  const BASE_WIDTH = 120;
  const BASE_HEIGHT = 104;
  const TILE_WIDTH = BASE_WIDTH * SCALE;
  const TILE_HEIGHT = BASE_HEIGHT * SCALE;

  const getTileImage = (r, c) => {
    const key = `${r}-${c}`;
    if (blueTiles.has(key)) return BLUE_TILE;
    if (whiteTiles.has(key)) return WHITE_TILE;
    return GOLD_TILE;
  };

  const resetGame = () => {
    setHasWon(false);
    setBlueTiles(new Set());
    setWhiteTiles(new Set());
    closeModal();
    setStage("welcome");
  };

  // Welcome music (optional)
  const audioRef = useRef(null);
  const [musicOn, setMusicOn] = useState(true);

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = 0.05;

    if (stage === "welcome" && musicOn) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [stage, musicOn]);

  // ---------- UI ----------
  if (stage === "welcome") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px 20px",
          background: "linear-gradient(to bottom, #f3f9ff, #dfefff)",
          textAlign: "center",
        }}
      >
        <audio ref={audioRef} src="/BlockBusters/Blockbusters_Theme.mp3" loop />

        <button
          onClick={() => setMusicOn((p) => !p)}
          style={{
            position: "absolute",
            top: 18,
            right: 18,
            background: "transparent",
            border: "none",
            fontSize: 48,
            cursor: "pointer",
          }}
          title={musicOn ? "Pause Music" : "Play Music"}
        >
          {musicOn ? "🔊" : "🔇"}
        </button>

        <img
          src="https://firebasestorage.googleapis.com/v0/b/ryan-tutors.firebasestorage.app/o/images%2FBlockbusters_logo.png?alt=media&token=4f862aa6-e973-4929-a3c6-13eb5bce0330"
          alt="Blockbusters Logo"
          style={{ width: 380, maxWidth: "90%", marginBottom: 20 }}
        />

        <h1 style={{ fontSize: 40, margin: "10px 0 24px", color: "#2c3e50" }}>
          Compound & Simple Interest
        </h1>

        <p style={{ maxWidth: 720, fontSize: 18, lineHeight: 1.6, color: "#2c3e50" }}>
          Choose a golden tile, answer the question, and turn it <b>blue</b>.  
          Make a continuous path from the <b>left</b> side to the <b>right</b> side to win.
          You get <b>two attempts</b> per tile.
        </p>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center", marginTop: 22 }}>
          <button
            onClick={() => setStage("game")}
            style={{
              padding: "14px 28px",
              fontSize: 18,
              fontWeight: 800,
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
              backgroundColor: "#ffd700",
              color: "#000",
            }}
          >
            🎮 Start Game
          </button>

          <button
            onClick={resetGame}
            style={{
              padding: "14px 28px",
              fontSize: 18,
              fontWeight: 800,
              borderRadius: 12,
              border: "none",
              cursor: "pointer",
              backgroundColor: "#87cefa",
              color: "#000",
            }}
          >
            🔁 Reset
          </button>
        </div>
      </div>
    );
  }

  // -------- GAME --------
  return (
    <div
      style={{
        padding: 20,
        fontFamily: "sans-serif",
        background: "linear-gradient(to bottom, rgb(83, 89, 95), rgb(183, 197, 210))",
        minHeight: "100vh",
      }}
    >
      <h1 style={{ textAlign: "center", marginBottom: 12 }}>Choose a golden tile</h1>

      <div style={{ display: "flex", justifyContent: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <button
          onClick={() => setStage("welcome")}
          style={{ padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer", fontWeight: 700 }}
        >
          ⬅️ Back to Welcome
        </button>

        <button
          onClick={() => {
            setHasWon(false);
            setBlueTiles(new Set());
            setWhiteTiles(new Set());
            closeModal();
          }}
          style={{ padding: "10px 16px", borderRadius: 10, border: "none", cursor: "pointer", fontWeight: 700 }}
        >
          🔁 Reset Board
        </button>
      </div>

      {/* GRID */}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {tileGrid.map((row, r) => (
            <div
              key={r}
              style={{
                display: "flex",
                gap: 2,
                marginLeft: r % 2 !== 0 ? 60 : 0,
              }}
            >
              {row.map((tile, c) => {
                const img = getTileImage(r, c);
                const isGold = img === GOLD_TILE;
                const key = `${r}-${c}`;

                return (
                  <div
                    key={key}
                    onClick={() => {
                      if (!isGold) return;
                      openTile(r, c);
                    }}
                    style={{
                      width: TILE_WIDTH,
                      height: TILE_HEIGHT,
                      position: "relative",
                      cursor: isGold ? "pointer" : "default",
                      filter: isGold ? "none" : "grayscale(0.15)",
                    }}
                  >
                    <img
                      src={img}
                      alt="tile"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />

                    {/* number label */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                        fontSize: 26,
                        color: img === GOLD_TILE ? "black" : "transparent", // hide on back tiles
                        userSelect: "none",
                      }}
                    >
                      {tile}
                    </div>

                    {/* hover overlay */}
                    {isGold && (
                      <div
                        style={{
                          position: "absolute",
                          inset: "2.5%",
                          backgroundColor: "rgba(255,255,255,0)",
                          clipPath:
                            "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
                          transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.25)")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0)")}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* WIN OVERLAY */}
      {hasWon && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.65)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
            color: "#ffd700",
            textAlign: "center",
            padding: 24,
          }}
        >
          <div style={{ fontSize: 60, fontWeight: 900 }}>YOU WIN!</div>
          <p style={{ color: "white", fontSize: 20, maxWidth: 680 }}>
            You connected the left side to the right side with blue tiles 🎉
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center", marginTop: 10 }}>
            <button
              onClick={() => setHasWon(false)}
              style={{ padding: "12px 20px", borderRadius: 10, border: "none", cursor: "pointer", fontWeight: 800 }}
            >
              ✅ Keep Playing
            </button>
            <button
              onClick={() => {
                setHasWon(false);
                setBlueTiles(new Set());
                setWhiteTiles(new Set());
              }}
              style={{
                padding: "12px 20px",
                borderRadius: 10,
                border: "none",
                cursor: "pointer",
                fontWeight: 800,
                background: "#ffd700",
              }}
            >
              🔁 New Board
            </button>
          </div>
        </div>
      )}

      {/* QUESTION MODAL */}
      {showModal && currentQ && selectedTile && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1500,
            padding: 16,
          }}
        >
          <div
            style={{
              background: "white",
              width: "min(900px, 96vw)",
              borderRadius: 14,
              padding: 18,
              display: "flex",
              flexDirection: "column",
              gap: 14,
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            {/* header row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ position: "relative", width: 70, height: 60, flexShrink: 0 }}>
                  <img
                    src={GOLD_TILE}
                    alt="gold tile"
                    style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }}
                  />
                  <span
                    style={{
                      position: "relative",
                      zIndex: 1,
                      fontSize: 26,
                      fontWeight: 900,
                      color: "black",
                      textAlign: "center",
                      lineHeight: "60px",
                      display: "block",
                    }}
                  >
                    {tileGrid[selectedTile.r][selectedTile.c]}
                  </span>
                </div>

                <div style={{ fontWeight: 800 }}>
                  Attempts: {attemptsOnThisTile}/2
                </div>

                {feedback === "correct" && (
                  <div style={{ color: "green", fontWeight: 900 }}>✅ Correct!</div>
                )}
                {feedback === "incorrect" && (
                  <div style={{ color: "crimson", fontWeight: 900 }}>
                    ❌ Incorrect{attemptsOnThisTile >= 1 ? " — tile will lock" : " — try again"}
                  </div>
                )}
              </div>

              <button
                onClick={closeModal}
                style={{ padding: "10px 14px", borderRadius: 10, border: "none", cursor: "pointer", fontWeight: 800 }}
              >
                ✖ Close
              </button>
            </div>

            {/* question */}
            <div style={{ borderTop: "1px solid #eee", paddingTop: 10 }}>
              {currentQ.promptLines.map((line, i) => (
                <div key={i} style={{ fontSize: 20, lineHeight: 1.6, marginBottom: 6 }}>
                  {looksLikeLatex(line) ? <BlockMath math={line} /> : line}
                </div>
              ))}
            </div>

            {/* inputs */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>Your Answer</div>
                <input
                  value={playerAnswer}
                  onChange={(e) => setPlayerAnswer(e.target.value)}
                  placeholder='e.g. 145 or £145'
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: 18,
                    borderRadius: 10,
                    border: "2px solid #ddd",
                    outline: "none",
                  }}
                />
                {feedback === "incorrect" && attemptsOnThisTile >= 1 && (
                  <div style={{ marginTop: 10, background: "#fff3f3", border: "1px solid #ffd2d2", padding: 10, borderRadius: 10 }}>
                    <b>Correct answer:</b>{" "}
                    {currentQ.accepted[0].toString().startsWith("\\") ? (
                      <BlockMath math={currentQ.accepted[0]} />
                    ) : (
                      <span>{currentQ.accepted[0]}</span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>Working</div>
                <textarea
                  value={playerWorking}
                  onChange={(e) => setPlayerWorking(e.target.value)}
                  placeholder="Show your working here..."
                  style={{
                    width: "100%",
                    minHeight: 120,
                    padding: "10px 12px",
                    fontSize: 16,
                    borderRadius: 10,
                    border: "2px solid #ddd",
                    resize: "vertical",
                    outline: "none",
                  }}
                />
              </div>
            </div>

            {/* actions */}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <button
                onClick={closeModal}
                style={{
                  padding: "12px 18px",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 900,
                  background: "#e6e6e6",
                }}
              >
                Cancel
              </button>

              <button
                onClick={submitAnswer}
                disabled={feedback === "correct"}
                style={{
                  padding: "12px 18px",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 900,
                  background: "#ffd700",
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BlockbustersCompoundInterest;
